import { getApiBaseUrl } from "./videoService.js";

/**
 * Converts a base64 string to a Uint8Array for VAPID applicationServerKey
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData =
    typeof window !== "undefined" && typeof window.atob === "function"
      ? window.atob(base64)
      : Buffer.from(base64, "base64").toString("binary");

  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if the current browser environment supports Push Notifications and Service Workers
 */
export function isPushNotificationSupported(): boolean {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Get current browser notification permission
 */
export function getNotificationPermission(): NotificationPermission {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "default";
  }
  return Notification.permission;
}

/**
 * Fetch the public VAPID key from the backend API
 */
export async function fetchVapidPublicKey(): Promise<string> {
  const baseUrl = getApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/v1/notifications/vapid-public-key`);
    if (!res.ok) {
      throw new Error(`Failed to fetch VAPID key: HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.publicKey;
  } catch (err) {
    console.warn("[push] Error fetching VAPID key, using fallback:", err);
    // Fallback key if offline or network error
    return "BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZElHKviAO_UvLP1x6nkeqwORasKYSWLUGWC2fQ";
  }
}

/**
 * Check if there is an active push subscription in the service worker
 */
export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch (err) {
    console.warn("[push] Error getting existing subscription:", err);
    return null;
  }
}

/**
 * Check full push subscription status
 */
export async function getPushSubscriptionStatus(): Promise<{
  supported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
}> {
  if (!isPushNotificationSupported()) {
    return { supported: false, permission: "denied", isSubscribed: false };
  }

  const permission = getNotificationPermission();
  const sub = await getExistingPushSubscription();
  return {
    supported: true,
    permission,
    isSubscribed: !!sub && permission === "granted",
  };
}

/**
 * Request permission and subscribe to Web Push Notifications
 */
export async function subscribeToPushNotifications(
  userId: string = "usr_current"
): Promise<{ success: boolean; subscription?: any; error?: string }> {
  if (!isPushNotificationSupported()) {
    return {
      success: false,
      error: "Push notifications are not supported in this browser",
    };
  }

  try {
    // 1. Request user permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return {
        success: false,
        error: `Notification permission was ${permission}`,
      };
    }

    // 2. Fetch VAPID key
    const publicKey = await fetchVapidPublicKey();
    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    // 3. Register push subscription with the browser's PushManager
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as unknown as BufferSource,
      });
    }

    // 4. Send subscription credentials to backend API
    const subJson = subscription.toJSON();
    const baseUrl = getApiBaseUrl();

    const response = await fetch(`${baseUrl}/api/v1/notifications/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth,
        },
        userAgent: navigator.userAgent,
      }),
    });

    if (!response.ok) {
      console.warn("[push] Backend subscription returned status:", response.status);
    }

    if (typeof localStorage !== "undefined") {
      localStorage.setItem("hp_push_subscribed", "true");
    }

    return { success: true, subscription: subJson };
  } catch (error: any) {
    console.error("[push] Failed to subscribe to push notifications:", error);
    return {
      success: false,
      error: error?.message || "Failed to subscribe to push notifications",
    };
  }
}

/**
 * Unsubscribe from Web Push Notifications
 */
export async function unsubscribeFromPushNotifications(): Promise<{
  success: boolean;
  error?: string;
}> {
  if (!isPushNotificationSupported()) {
    return { success: true };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const baseUrl = getApiBaseUrl();
      // Notify backend to remove subscription
      try {
        await fetch(`${baseUrl}/api/v1/notifications/unsubscribe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      } catch (err) {
        console.warn("[push] Failed to notify backend of unsubscription:", err);
      }

      // Unsubscribe locally from browser PushManager
      await subscription.unsubscribe();
    }

    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("hp_push_subscribed");
    }

    return { success: true };
  } catch (error: any) {
    console.error("[push] Failed to unsubscribe:", error);
    return {
      success: false,
      error: error?.message || "Failed to unsubscribe",
    };
  }
}

/**
 * Send a test push notification to verify the engine end-to-end
 */
export async function sendTestPushNotification(
  userId: string = "usr_current"
): Promise<{ success: boolean; sent?: number; failed?: number; error?: string }> {
  try {
    const baseUrl = getApiBaseUrl();
    const res = await fetch(`${baseUrl}/api/v1/notifications/test`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        title: "HP - Helix Video",
        body: "Test notification: Web Push is configured and working!",
        url: "/",
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    return {
      success: data.success,
      sent: data.sent,
      failed: data.failed,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || "Failed to send test push notification",
    };
  }
}
